import { useMutation } from '@tanstack/react-query'

import type { StreamChatRequest } from '../../types/index.js'

import { apiClient } from '../../lib/client.js'
import { chatKeys } from './keys.js'

export interface StreamChatMutationResult {
  content: string
}

export interface StreamChatMutationVariables extends StreamChatRequest {
  onChunk?: (chunk: string) => void
  signal?: AbortSignal
}

const MAX_STREAM_URL_LENGTH = 7000

export async function streamChatMutationFn({
  messages,
  onChunk,
  signal,
}: StreamChatMutationVariables): Promise<StreamChatMutationResult> {
  const baseUrl = apiClient.client.defaults.baseURL ?? ''
  const params = new URLSearchParams({
    messages: JSON.stringify(messages),
  })

  const streamUrl = `${baseUrl}/chat/stream?${params.toString()}`
  if (streamUrl.length > MAX_STREAM_URL_LENGTH) {
    throw new Error(
      'Conversation trop longue pour le mode streaming. Merci de redemarrer une nouvelle conversation.'
    )
  }

  const response = await fetch(streamUrl, {
    credentials: 'include',
    signal,
  })

  if (!response.ok) {
    let backendMessage: string | undefined

    try {
      const payload = (await response.json()) as { message?: string }
      if (typeof payload.message === 'string') {
        backendMessage = payload.message
      }
    } catch {
      const fallbackText = await response.text().catch(() => '')
      if (fallbackText) {
        backendMessage = fallbackText
      }
    }

    if (response.status === 429) {
      throw new Error(backendMessage ?? 'Trop de messages envoyés. Veuillez patienter.')
    }

    throw new Error(backendMessage ?? 'Stream connection failed')
  }

  if (!response.body) {
    throw new Error('Stream connection failed')
  }

  const reader = response.body.getReader()
  const decoder = new TextDecoder()
  let content = ''
  let buffer = ''

  while (true) {
    const { done, value } = await reader.read()
    if (done) {
      break
    }

    buffer += decoder.decode(value, { stream: true })
    const events = buffer.split('\n\n')
    buffer = events.pop() ?? ''

    for (const event of events) {
      const lines = event.split('\n')
      for (const line of lines) {
        if (!line.startsWith('data:')) {
          continue
        }

        const chunk = line.slice(5).trimStart()
        if (!chunk) {
          continue
        }

        content += chunk
        onChunk?.(chunk)
      }
    }
  }

  if (buffer) {
    const lines = buffer.split('\n')
    for (const line of lines) {
      if (!line.startsWith('data:')) {
        continue
      }
      const chunk = line.slice(5).trimStart()
      if (!chunk) {
        continue
      }
      content += chunk
      onChunk?.(chunk)
    }
  }

  return { content }
}

export function useStreamChatMutation() {
  return useMutation({
    mutationFn: streamChatMutationFn,
    mutationKey: chatKeys.stream,
  })
}
